// Copyright (c) Ping. All rights reserved.
//
// Spotlight layout — a big main stage on the left + a vertical, scrollable
// participant filmstrip on the right. Used for BOTH screen-share (Figma Desktop
// 2444:6995 "share_screen maximized") and a pinned participant (Figma DS
// 4389:98843 pin action): pinning promotes one card to the stage and pushes the
// rest into the right panel, exactly like the share-screen layout. Screen shares
// get zoom + pan (Figma "Zoom option" 2444:7103). GPU transforms, no per-frame
// React state. Web-only.

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, Pressable, ScrollView, Text, View } from 'react-native';

import type { Track } from 'livekit-client';
import { LoaderCircle, Maximize2, MonitorUp, ZoomIn, ZoomOut } from 'lucide-react-native';

import type { LiveKitParticipantView } from '@calls/livekit/LiveKitCallClient';
import { useCallStore } from '@calls/store/callStore';

import { CallVideoTile } from './CallVideoTile';
import { ParticipantTile } from './ParticipantTile';

const MAX_SCALE = 4;
const FILMSTRIP_W = 240;
const MINIMAP_W = 240; // Figma "Zoom option" panel width (907:14030)

interface FsEl extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}
interface FsDoc extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}
/** Toggle browser fullscreen on the call surface (Safari-safe webkit fallbacks). */
function toggleScreenFullscreen(): void {
  if (typeof document === 'undefined') return;
  const doc = document as FsDoc;
  const cur = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
  if (cur !== null) {
    void Promise.resolve(doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.()).catch(() => undefined);
  } else {
    const el = document.documentElement as FsEl;
    void Promise.resolve(el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.()).catch(() => undefined);
  }
}

/** Bottom-left presenter name tag (Figma 4399:81005/81279). */
function PresenterTag({ name }: { name: string }): JSX.Element {
  return (
    <View className="absolute bottom-[16px] left-[16px] flex-row items-center gap-[8px] rounded-[10px] bg-black/55 px-[12px] py-[6px]">
      <Text className="text-[15px] font-bold text-[#F5F6F8]">{name}</Text>
      <MonitorUp size={16} color="#E8EAED" />
    </View>
  );
}

/** Zoomable + pannable shared-screen surface with the DS zoom control bar +
 *  a navigation minimap (Figma zoom-option 907:14030). All motion is written
 *  straight to the DOM (transform + minimap rect) — no per-frame React state. */
export function ZoomableScreen({ track }: { track: Track | undefined }): JSX.Element {
  const [scale, setScale] = useState(1);
  const [hover, setHover] = useState(false);
  const [sliderActive, setSliderActive] = useState(false);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const minimapElRef = useRef<HTMLDivElement | null>(null);
  const mapRectRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const pan = useRef({ x: 0, y: 0 });
  const drag = useRef<null | { sx: number; sy: number; ox: number; oy: number }>(null);
  // Relative-drag anchor for panning FROM the minimap.
  const mapDrag = useRef<null | { cx: number; cy: number; px: number; py: number }>(null);
  const sliderDrag = useRef(false);

  const clampPan = useCallback((s: number) => {
    const el = innerRef.current;
    if (!el) return;
    const maxX = (el.clientWidth * (s - 1)) / 2;
    const maxY = (el.clientHeight * (s - 1)) / 2;
    pan.current.x = Math.max(-maxX, Math.min(maxX, pan.current.x));
    pan.current.y = Math.max(-maxY, Math.min(maxY, pan.current.y));
  }, []);
  // Writes the live transform + minimap rect straight to the DOM (no React
  // state per frame → buttery, leak-free panning). The rect uses the minimap's
  // MEASURED size so the math is exact at any layout width.
  const applyTransform = useCallback((s: number) => {
    const el = innerRef.current;
    if (el) el.style.transform = `translate(${pan.current.x}px, ${pan.current.y}px) scale(${s})`;
    const rect = mapRectRef.current;
    const map = minimapElRef.current;
    if (rect && el && map) {
      const mw = map.clientWidth;
      const mh = map.clientHeight;
      const fx = el.clientWidth > 0 ? pan.current.x / (el.clientWidth * s) : 0;
      const fy = el.clientHeight > 0 ? pan.current.y / (el.clientHeight * s) : 0;
      const w = mw / s;
      const h = mh / s;
      rect.style.width = `${w}px`;
      rect.style.height = `${h}px`;
      rect.style.left = `${(0.5 - fx) * mw - w / 2}px`;
      rect.style.top = `${(0.5 - fy) * mh - h / 2}px`;
    }
  }, []);

  useEffect(() => {
    if (scale === 1) pan.current = { x: 0, y: 0 };
    clampPan(scale);
    applyTransform(scale);
  }, [scale, clampPan, applyTransform]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (scale <= 1) return;
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pan.current.x, oy: pan.current.y };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    pan.current.x = d.ox + (e.clientX - d.sx);
    pan.current.y = d.oy + (e.clientY - d.sy);
    clampPan(scale);
    applyTransform(scale);
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  // ── Minimap panning — relative drag, written straight to the DOM. Grab
  //    anywhere on the minimap and the main view follows precisely (moving the
  //    minimap viewport by X% pans the content by X%). No per-move React state.
  const onMapDown = (e: React.PointerEvent) => {
    if (scale <= 1) return;
    mapDrag.current = { cx: e.clientX, cy: e.clientY, px: pan.current.x, py: pan.current.y };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onMapMove = (e: React.PointerEvent) => {
    const d = mapDrag.current;
    const map = minimapElRef.current;
    const inner = innerRef.current;
    if (!d || !map || !inner) return;
    const r = map.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    const dx = (e.clientX - d.cx) / r.width;
    const dy = (e.clientY - d.cy) / r.height;
    pan.current.x = d.px - dx * inner.clientWidth * scale;
    pan.current.y = d.py - dy * inner.clientHeight * scale;
    clampPan(scale);
    applyTransform(scale);
  };
  const onMapUp = () => {
    mapDrag.current = null;
  };

  // ── Footer zoom slider (Figma 907:14030). Maps the pointer x → scale.
  const scaleFromSlider = (clientX: number): number => {
    const el = sliderRef.current;
    if (!el) return scale;
    const r = el.getBoundingClientRect();
    const p = r.width > 0 ? Math.max(0, Math.min(1, (clientX - r.left) / r.width)) : 0;
    return Math.round((1 + p * (MAX_SCALE - 1)) * 100) / 100;
  };
  const onSliderDown = (e: React.PointerEvent) => {
    sliderDrag.current = true;
    setSliderActive(true);
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    setScale(scaleFromSlider(e.clientX));
  };
  const onSliderMove = (e: React.PointerEvent) => {
    if (!sliderDrag.current) return;
    setScale(scaleFromSlider(e.clientX));
  };
  const onSliderUp = () => {
    sliderDrag.current = false;
    setSliderActive(false);
  };
  const step = (delta: number) =>
    setScale((s) => Math.max(1, Math.min(MAX_SCALE, Math.round((s + delta) * 100) / 100)));

  const pct = Math.round(scale * 100);
  const zoomed = scale > 1;
  // Fill / thumb position along the slider (0→1 across 100%→MAX).
  const sliderPct = (scale - 1) / (MAX_SCALE - 1);

  return (
    <View className="relative flex-1 overflow-hidden rounded-[12px] border border-white/[0.06] bg-black">
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: '100%',
          height: '100%',
          cursor: zoomed ? (drag.current ? 'grabbing' : 'grab') : 'default',
        }}
      >
        <div
          ref={innerRef}
          style={{ width: '100%', height: '100%', transformOrigin: 'center', willChange: 'transform' }}
        >
          <CallVideoTile track={track} contain />
        </div>
      </div>

      {/* Maximize-on-hover pill (Figma hover state 4399:81003). */}
      {hover && !zoomed ? (
        <View style={{ position: 'absolute', inset: 0, pointerEvents: 'box-none' } as never} className="items-center justify-center">
          <Pressable
            accessibilityLabel="Maximize screen"
            onPress={toggleScreenFullscreen}
            className="flex-row items-center gap-[8px] rounded-[12px] bg-black/65 px-[16px] py-[10px] active:opacity-80"
          >
            <Text className="text-[15px] font-bold text-[#F5F6F8]">Maximize screen</Text>
            <Maximize2 size={14} color="#E8EAED" />
          </Pressable>
        </View>
      ) : null}

      {/* Navigation minimap + zoom footer (Figma "Zoom option" 907:14030),
          bottom-right. The same track is re-attached to a small <video> (shared
          decode, no double cost). Grab the minimap to pan the main view; the blue
          viewport rect tracks pan/zoom. Footer = zoom-out · slider · zoom-in. */}
      {track !== undefined ? (
        <View
          style={{ position: 'absolute', right: 16, bottom: 16, width: MINIMAP_W }}
          className="gap-[4px] rounded-[12px] border overflow-hidden border-dark-surface-pressed bg-dark-surface-panel shadow-lg"
        >
          {/* Thumbnail (grab to pan) */}
          <div
            ref={minimapElRef}
            onPointerDown={onMapDown}
            onPointerMove={onMapMove}
            onPointerUp={onMapUp}
            onPointerCancel={onMapUp}
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 9',
              borderRadius: 12,
              overflow: 'hidden',
              cursor: zoomed ? (mapDrag.current ? 'grabbing' : 'grab') : 'default',
            }}
          >
            <CallVideoTile track={track} contain />
            <div
              ref={mapRectRef}
              style={{
                position: 'absolute',
                boxSizing: 'border-box',
                border: '1px solid #537EFF',
                backgroundColor: 'rgba(83,126,255,0.05)',
                borderRadius: 12,
                pointerEvents: 'none',
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
              }}
            />
          </div>

          {/* Footer: zoom-out · slider · zoom-in */}
          <View className="flex-row items-center gap-[8px] bg-[#ECEEF1] dark:bg-[#111111] py-0.5">
            <Pressable
              accessibilityLabel="Zoom out"
              onPress={() => step(-0.25)}
              className="h-[28px] w-[28px] items-center justify-center rounded-[8px] active:bg-white/[0.06]"
            >
              <ZoomOut size={14} color="#E8EAED" />
            </Pressable>

            {/* Custom track + fill + thumb (design-accurate) */}
            <div
              ref={sliderRef}
              role="slider"
              aria-label="Zoom level"
              aria-valuemin={100}
              aria-valuemax={MAX_SCALE * 100}
              aria-valuenow={pct}
              onPointerDown={onSliderDown}
              onPointerMove={onSliderMove}
              onPointerUp={onSliderUp}
              onPointerCancel={onSliderUp}
              style={{
                position: 'relative',
                flex: 1,
                height: 16,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                touchAction: 'none',
              }}
            >
              <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 999, background: '#222429' }} />
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  height: 4,
                  width: `${sliderPct * 100}%`,
                  borderRadius: 999,
                  background: '#537EFF',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: `${sliderPct * 100}%`,
                  width: 13,
                  height: 13,
                  marginLeft: -6.5,
                  borderRadius: 999,
                  background: '#E8EAED',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }}
              />
              {sliderActive ? (
                <View
                  style={{ position: 'absolute', left: `${sliderPct * 100}%`, bottom: 18, transform: [{ translateX: -19 }] }}
                  className="items-center"
                >
                  <View className="rounded-[8px] bg-dark-surface-elevated px-[8px] py-[4px]">
                    <Text className="text-[12px] font-bold text-[#F5F6F8]">{pct}%</Text>
                  </View>
                </View>
              ) : null}
            </div>

            <Pressable
              accessibilityLabel="Zoom in"
              onPress={() => step(0.25)}
              className="h-[28px] w-[28px] items-center justify-center rounded-[8px] active:bg-white/[0.06]"
            >
              <ZoomIn size={14} color="#E8EAED" />
            </Pressable>
          </View>
        </View>
      ) : null}
    </View>
  );
}

/** GPU-cheap rotating loader (the lucide glyph doesn't self-animate). */
function Spinner({ size = 44 }: { size?: number }): JSX.Element {
  const spin = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(spin, { toValue: 1, duration: 900, easing: Easing.linear, useNativeDriver: false }),
    );
    loop.start();
    return () => loop.stop();
  }, [spin]);
  const rotate = spin.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });
  return (
    <Animated.View style={{ width: size, height: size, transform: [{ rotate }] }}>
      <LoaderCircle size={size} color="#537EFF" strokeWidth={4} />
    </Animated.View>
  );
}

/** Screen-share stage: zoomable surface + bottom-left presenter tag + controls.
 *  `pinnedOverlay` (optional) is a participant tile shown prominently over the
 *  screen, so a viewer can pin someone WHILE watching a share. */
export function ScreenStage({
  presenter,
  pinnedOverlay,
}: {
  presenter: LiveKitParticipantView;
  pinnedOverlay?: ReactNode;
}): JSX.Element {
  const isLocal = presenter.isLocal;
  return (
    <View className="relative flex-1">
      <ZoomableScreen track={presenter.screenTrack} />
      <PresenterTag name={isLocal ? 'You' : presenter.name} />
      {pinnedOverlay !== undefined ? (
        <View className="absolute left-[16px] top-[16px] aspect-video w-[260px] overflow-hidden rounded-[12px] border border-white/[0.12] shadow-lg">
          {pinnedOverlay}
        </View>
      ) : null}
      {isLocal ? (
        <View className="absolute right-[12px] top-[12px] flex-row items-center gap-[8px]">
          <Pressable
            accessibilityLabel="Change screen"
            onPress={() => void useCallStore.getState().changeScreenShare()}
            className="h-[32px] flex-row items-center justify-center rounded-[8px] border border-dark-surface-pressed bg-dark-surface-elevated px-[12px] active:opacity-80"
          >
            <Text className="text-[13px] font-semibold text-[#F5F6F8]">Change screen</Text>
          </Pressable>
          <Pressable
            accessibilityLabel="Stop sharing screen"
            onPress={() => void useCallStore.getState().toggleScreenShare()}
            className="h-[32px] flex-row items-center justify-center rounded-[8px] bg-[#FF5353] px-[12px] active:opacity-80"
          >
            <Text className="text-[13px] font-bold text-white">Stop sharing</Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
}

/** Loading state (Figma 4399:81279) — dark surface, centered spinner, name tag. */
export function ScreenShareConnecting({ name }: { name?: string }): JSX.Element {
  return (
    <View className="relative flex-1 items-center justify-center rounded-[12px] border border-white/[0.06] bg-dark-surface-elevated">
      <Spinner />
      {name !== undefined ? <PresenterTag name={name} /> : null}
    </View>
  );
}

/** The generic spotlight: `stage` on the left, participant filmstrip on right. */
export function SpotlightLayout({
  stage,
  filmstrip,
  speakingSet,
  pinnedIdentity,
  onTogglePin,
  selfHand,
  selfAway,
  raisedHands,
  awayParticipants,
  videoIdentities,
}: {
  stage: React.ReactNode;
  filmstrip: LiveKitParticipantView[];
  speakingSet: Set<string>;
  pinnedIdentity: string | null;
  onTogglePin: (identity: string) => void;
  selfHand: boolean;
  selfAway: boolean;
  raisedHands: Record<string, boolean>;
  awayParticipants: Record<string, boolean>;
  /** Identities allowed to decode live video (decode budget). undefined → all. */
  videoIdentities?: ReadonlySet<string>;
}): JSX.Element {
  return (
    <View className="flex-1 flex-row gap-[12px]">
      <View className="flex-1">{stage}</View>
      {filmstrip.length > 0 ? (
        <View style={{ width: FILMSTRIP_W }}>
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ gap: 12 }}>
            {filmstrip.map((p) => (
              <View key={p.identity} className="aspect-video w-full">
                <ParticipantTile
                  p={p}
                  compact
                  cameraOnly
                  speaking={speakingSet.has(p.identity)}
                  handRaised={p.isLocal ? selfHand : !!raisedHands[p.identity]}
                  away={p.isLocal ? selfAway : !!awayParticipants[p.identity]}
                  pinned={pinnedIdentity === p.identity}
                  onTogglePin={onTogglePin}
                  allowVideo={videoIdentities === undefined || videoIdentities.has(p.identity)}
                />
              </View>
            ))}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
}
